import type { DragonDossier } from "../types";
import { FactionGraph } from "./FactionGraph";
import { Icon, type IconName } from "./Icon";

interface OverviewProps {
  dossier: DragonDossier;
  selectedFactionId: string | null;
  onSelectFaction: (id: string) => void;
  onMoveFaction: (id: string, x: number, y: number) => void;
}

export function Overview({
  dossier,
  selectedFactionId,
  onSelectFaction,
  onMoveFaction
}: OverviewProps) {
  const distribution = dossier.hoard.distribution;
  const timelineIcons: IconName[] = ["gem", "spark", "domain", "lair", "dragon", "compass"];
  return (
    <div className="overview-grid">
      <section className="dashboard-panel history-preview">
        <h2>History timeline</h2>
        <div className="timeline">
          {dossier.history.slice(0, 6).map((event, index) => (
            <article className="timeline-event" key={event.id}>
              <div className="timeline-event__marker">
                <Icon name={timelineIcons[index % timelineIcons.length]} />
              </div>
              <div>
                <strong>{event.era ? `${event.era} · ` : ""}{event.age} years</strong>
                <p>{event.title}</p>
                <small>{event.description}</small>
                {event.consequence ? <small className="timeline-event__consequence">{event.consequence}</small> : null}
                {event.linkedEntities?.length ? (
                  <div className="timeline-links" aria-label={`Linked entities for ${event.title}`}>
                    {event.linkedEntities.slice(0, 3).map((link) => (
                      <span key={`${event.id}-${link.type}-${link.id}`}>{link.type}: {link.name}</span>
                    ))}
                  </div>
                ) : null}
              </div>
            </article>
          ))}
        </div>
      </section>
      <section className="dashboard-panel hoard-preview">
        <h2>Hoard preview</h2>
        <div className="hoard-total">
          <strong>{dossier.hoard.totalValue.toLocaleString("en-US")} gp</strong>
          <span>Estimated total value</span>
        </div>
        <div className="hoard-chart-row">
          <div
            className="hoard-donut"
            style={{
              background: `conic-gradient(var(--accent, #bc633e) 0 ${distribution.visible}%, var(--gold-400, #c69853) ${distribution.visible}% ${
                distribution.visible + distribution.hidden
              }%, #8a5a34 ${distribution.visible + distribution.hidden}% ${
                distribution.visible + distribution.hidden + distribution.claimed
              }%, #5d4a63 0)`
            }}
          />
          <dl className="hoard-legend">
            <div>
              <dt>Visible</dt>
              <dd>{distribution.visible}%</dd>
            </div>
            <div>
              <dt>Hidden</dt>
              <dd>{distribution.hidden}%</dd>
            </div>
            <div>
              <dt>Claimed</dt>
              <dd>{distribution.claimed}%</dd>
            </div>
            <div>
              <dt>Cursed / trapped</dt>
              <dd>{distribution.cursed}%</dd>
            </div>
          </dl>
        </div>
        {dossier.hoard.items[0] ? (
          <article className="signature-item">
            <div className="signature-item__art">
              <Icon name="gem" />
            </div>
            <div>
              <strong>{dossier.hoard.items[0].name}</strong>
              <span>{dossier.hoard.items[0].rarity}</span>
              <p>{dossier.hoard.items[0].complication}</p>
            </div>
          </article>
        ) : null}
      </section>
      <section className="dashboard-panel faction-preview">
        <h2>Factions &amp; influence</h2>
        <FactionGraph
          dragonName={dossier.dragon.name}
          factions={dossier.factions}
          relationships={dossier.relationships}
          accent={dossier.appearance.accent}
          selectedId={selectedFactionId}
          onSelect={onSelectFaction}
          onMove={onMoveFaction}
        />
        <div className="relation-legend">
          {["worships", "fears", "bargains", "rivals", "spies"].map((relation, index) => (
            <span key={relation} style={{ color: dossier.factions[index]?.color }}>
              — {relation}
            </span>
          ))}
        </div>
      </section>
      <section className="dashboard-panel encounter-preview">
        <h2>Encounter script</h2>
        <div className="encounter-stack">
          {dossier.encounterScript.map((phase) => (
            <article className={`encounter-card encounter-card--${phase.kind}`} key={phase.id}>
              <div className="encounter-card__icon">
                <Icon
                  name={
                    phase.kind === "social"
                      ? "faction"
                      : phase.kind === "stealth"
                        ? "moon"
                        : phase.kind === "combat"
                          ? "threat"
                          : phase.kind === "parley"
                            ? "share"
                            : phase.kind === "escape"
                              ? "export"
                              : "hoard"
                  }
                />
              </div>
              <div>
                <strong>{phase.title}</strong>
                <ul>
                  {phase.steps.slice(0, 2).map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
